import { BulkUploadResultStatus, BulkUploadStatus } from "../constants/constants";

/**
 * One row's failure from a bulk user CSV upload.
 *
 * `reason` is empty for rows recorded before per-row reasons existed, so callers should say the
 * reason is unavailable rather than invent one.
 */
export interface BulkUploadFailedEntry {
    email?: string;
    reason?: string;
    detail?: string;
}

/**
 * A single bulk user CSV upload and its processing outcome.
 *
 * Every field is optional: the wire shape this is adapted from reads through optional chaining and
 * tolerates a missing payload, so nothing here is guaranteed to be present and callers must render
 * a fallback rather than dereference directly.
 *
 * The result fields (`resultStatus`, `recordCount`, `processedRowCount`, `successCount`,
 * `failedCount`, `failedEmails`, `failedEmailsRetained`, `appliedUserGroupIds`,
 * `rejectedUserGroupIds`) are populated only once the upload has been processed.
 *
 * `startTime` and `endTime` are written by the blob-trigger job when processing begins and ends,
 * so a queued upload has neither and an in-flight one has only `startTime`.
 */
export class BulkUpload {
    constructor({
        fileIdentifier,
        organizationId,
        originalFileName,
        uploaderEmail,
        uploadedAt,
        status,
        resultStatus,
        recordCount,
        processedRowCount,
        successCount,
        failedCount,
        failedEmails,
        failedEmailsRetained,
        startTime,
        endTime,
        appliedUserGroupIds,
        rejectedUserGroupIds
    }: {
        fileIdentifier?: string;
        organizationId?: string;
        originalFileName?: string;
        uploaderEmail?: string;
        uploadedAt?: Date;
        status?: BulkUploadStatus;
        resultStatus?: BulkUploadResultStatus;
        recordCount?: number;
        processedRowCount?: number;
        successCount?: number;
        failedCount?: number;
        failedEmails?: BulkUploadFailedEntry[];
        failedEmailsRetained?: number;
        startTime?: Date;
        endTime?: Date;
        appliedUserGroupIds?: string[];
        rejectedUserGroupIds?: string[];
    }) {
        this.fileIdentifier = fileIdentifier;
        this.organizationId = organizationId;
        this.originalFileName = originalFileName;
        this.uploaderEmail = uploaderEmail;
        this.uploadedAt = uploadedAt;
        this.status = status;
        this.resultStatus = resultStatus;
        this.recordCount = recordCount;
        this.processedRowCount = processedRowCount;
        this.successCount = successCount;
        this.failedCount = failedCount;
        this.failedEmails = failedEmails;
        this.failedEmailsRetained = failedEmailsRetained;
        this.startTime = startTime;
        this.endTime = endTime;
        this.appliedUserGroupIds = appliedUserGroupIds;
        this.rejectedUserGroupIds = rejectedUserGroupIds;
    }

    fileIdentifier?: string;
    organizationId?: string;
    originalFileName?: string;
    uploaderEmail?: string;
    uploadedAt?: Date;
    status?: BulkUploadStatus;
    resultStatus?: BulkUploadResultStatus;
    recordCount?: number;
    processedRowCount?: number;
    successCount?: number;
    failedCount?: number;
    /**
     * The failures the job retained. Bounded independently of `failedCount`, which stays the
     * authoritative total — see `failedEmailsRetained`.
     */
    failedEmails?: BulkUploadFailedEntry[];
    /**
     * How many entries `failedEmails` actually holds after bounding. When this is below
     * `failedCount` the list is a truncated sample, not the whole set.
     */
    failedEmailsRetained?: number;
    startTime?: Date;
    endTime?: Date;
    /** Target user groups that were valid at process time and applied to every processed user. */
    appliedUserGroupIds?: string[];
    /** Target user groups rejected at process time (deleted, cross-org, or a disallowed type). */
    rejectedUserGroupIds?: string[];
}
